import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { t } from '../i18n.js';
import { api } from '../state/auth.js';
import { notifications, loadNotifications, markAsRead, markAllAsRead } from '../state/notifications.js';
import Button from '../components/shared/Button.jsx';
import { parseServerDate } from '../utils/dates.js';
import { PushBanner } from '../components/shared/PushSettings.jsx';

function timeAgo(dateStr) {
  const diff = Date.now() - parseServerDate(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'zojuist';
  if (mins < 60) return `${mins}m geleden`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}u geleden`;
  const days = Math.floor(hours / 24);
  return `${days}d geleden`;
}

const NOTIFICATION_TYPE_KEYS = {
  task_assigned: 'notification.taskAssigned',
  task_delegated: 'notification.taskDelegated',
  process_started: 'notification.processStarted',
  process_completed: 'notification.processCompleted',
  step_completed: 'notification.stepCompleted',
  new_document: 'notification.newDocument',
};

const ACTIVITY_ICONS = {
  task_created:      '✅',
  task_completed:    '✅',
  process_started:   '⚙️',
  process_completed: '🏁',
  process_cancelled: '❌',
  step_completed:    '✓',
  member_joined:     '👤',
  news_posted:       '📰',
  event_created:     '📅',
  document_uploaded: '📄',
};

const ACTIVITY_LABELS = {
  task_created:      (a) => `heeft taak aangemaakt: "${a.title}"`,
  task_completed:    (a) => `heeft taak afgerond: "${a.title}"`,
  process_started:   (a) => `heeft proces gestart: "${a.title}"`,
  process_completed: (a) => `heeft proces afgerond: "${a.title}"`,
  process_cancelled: (a) => `heeft proces geannuleerd: "${a.title}"`,
  step_completed:    (a) => `heeft stap voltooid: "${a.title}"`,
  member_joined:     ()  => `is toegevoegd aan de club`,
  news_posted:       (a) => `heeft een nieuwsbericht geplaatst: "${a.title}"`,
  event_created:     (a) => `heeft een evenement aangemaakt: "${a.title}"`,
  document_uploaded: (a) => `heeft een document geüpload: "${a.title}"`,
};

// ── Personal Notifications Tab ────────────────────────────────────────────────

// The shared state (src/state/notifications.js) keeps the header badge in step with this page
function PersonalNotificationsTab() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNotifications().finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div class="text-center text-muted" style="padding:40px">{t('common.loading')}</div>;
  }

  return (
    <NotificationList
      notifications={notifications.value}
      onMarkRead={id => markAsRead(id).catch(() => {})}
      onMarkAllRead={() => markAllAsRead().catch(() => {})}
    />
  );
}

export function NotificationList({ notifications, onMarkRead, onMarkAllRead }) {
  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div>
      <PushBanner />
      {unreadCount > 0 && (
        <div style="display:flex;justify-content:flex-end;margin-bottom:12px">
          <Button variant="secondary" size="sm" onClick={onMarkAllRead}>
            {t('notification.markAllRead')}
          </Button>
        </div>
      )}
      {notifications.length === 0 ? (
        <div class="text-center text-muted" style="padding:40px">{t('notification.noNotifications')}</div>
      ) : (
        <div class="notification-list">
          {notifications.map(n => (
            <div
              key={n.id}
              class={`notification-item ${!n.is_read ? 'notification-item-unread' : ''}`}
              onClick={() => !n.is_read && onMarkRead(n.id)}
            >
              <div class="notification-item-body">
                <div class="notification-item-title">
                  {NOTIFICATION_TYPE_KEYS[n.type]
                    ? t(NOTIFICATION_TYPE_KEYS[n.type])
                    : n.title}
                </div>
                <div class="notification-item-message">{n.message}</div>
                <div class="notification-item-time text-sm text-muted">{timeAgo(n.created_at)}</div>
              </div>
              {!n.is_read && <span class="notification-item-dot" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Club Activity Tab ─────────────────────────────────────────────────────────

function ClubActivityTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api('/api/activity?limit=50')
      .then(data => setItems(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div class="text-center text-muted" style="padding:40px">{t('common.loading')}</div>;
  }

  if (items.length === 0) {
    return (
      <div class="text-center text-muted" style="padding:40px">
        Nog geen clubactiviteit om te tonen.
      </div>
    );
  }

  return (
    <div class="notification-list">
      {items.map(item => {
        const icon = ACTIVITY_ICONS[item.type] || '•';
        const labelFn = ACTIVITY_LABELS[item.type];
        const label = labelFn ? labelFn(item) : item.title;
        return (
          <div key={item.id} class="notification-item" style="cursor:default">
            <div style="width:36px;height:36px;border-radius:50%;background:var(--color-bg-secondary);border:1px solid var(--color-border);display:flex;align-items:center;justify-content:center;font-size:15px;flex-shrink:0">
              {icon}
            </div>
            <div class="notification-item-body" style="margin-left:10px">
              <div class="notification-item-title" style="font-weight:normal;font-size:13px">
                <strong style="font-weight:600">{item.actor_name}</strong>
                {' '}
                <span style="color:var(--color-text-muted)">{label}</span>
              </div>
              <div class="notification-item-time text-sm text-muted">{timeAgo(item.created_at)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main View ─────────────────────────────────────────────────────────────────

export default function NotificationsView() {
  const [activeTab, setActiveTab] = useState('personal');

  const tabStyle = (tab) => ({
    padding: '8px 16px',
    background: 'none',
    border: 'none',
    borderBottom: activeTab === tab ? '2px solid var(--color-primary)' : '2px solid transparent',
    color: activeTab === tab ? 'var(--color-primary)' : 'var(--color-text-muted)',
    fontWeight: activeTab === tab ? '600' : '400',
    fontSize: '14px',
    cursor: 'pointer',
    marginBottom: '-1px',
  });

  return (
    <div>
      <div class="page-header">
        <h1 class="page-title">{t('notification.title')}</h1>
      </div>

      {/* Tabs */}
      <div style="display:flex;border-bottom:1px solid var(--color-border);margin-bottom:20px">
        <button style={tabStyle('personal')} onClick={() => setActiveTab('personal')}>
          🔔 Mijn notificaties
        </button>
        <button style={tabStyle('activity')} onClick={() => setActiveTab('activity')}>
          🕐 Clubactiviteit
        </button>
      </div>

      {activeTab === 'personal' ? <PersonalNotificationsTab /> : <ClubActivityTab />}
    </div>
  );
}
